// Foto de historia de Instagram de una farmacia (§8): vertical 1080×1920, JPG.
// Se recorta tipo «cover» y se pasa a JPG en el navegador; no usa compressImage
// (ese saca WebP de 1200 px). Estados visibles: optimizando / subiendo / error (§4.8).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Image as ImageIcon } from "lucide-react";
import { adminFetch } from "../api";
import { Button, Field, ICON, Spinner, cx, useToast } from "./ui";

const ANCHO = 1080;
const ALTO = 1920;

/** Dibuja la imagen en 1080×1920 con recorte centrado (si ya es 9:16 no se recorta) y la pasa a JPG 0.88. */
async function aJpgHistoria(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const canvas = document.createElement("canvas");
    canvas.width = ANCHO;
    canvas.height = ALTO;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("El navegador no pudo preparar la imagen.");
    const escala = Math.max(ANCHO / img.naturalWidth, ALTO / img.naturalHeight);
    const w = img.naturalWidth * escala;
    const h = img.naturalHeight * escala;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, ANCHO, ALTO);
    ctx.drawImage(img, (ANCHO - w) / 2, (ALTO - h) / 2, w, h);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("No se pudo generar el JPG."))), "image/jpeg", 0.88),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const FotoHistoria: React.FC<{ pharmacyId: string }> = ({ pharmacyId }) => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [thumb, setThumb] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<"idle" | "optimizing" | "uploading" | "removing">("idle");
  const [error, setError] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const busy = step !== "idle";

  // La miniatura es un object URL: se cambia con setThumb y se revoca al reemplazarla y al desmontar.
  const ponerThumb = useCallback((blob: Blob | null) => {
    setThumb((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return blob ? URL.createObjectURL(blob) : null;
    });
  }, []);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setError(null);
    adminFetch("/api/historia-foto/" + pharmacyId)
      .then(async (res) => {
        if (!vivo) return;
        if (res.status === 404) return ponerThumb(null);
        if (!res.ok) throw new Error(`El servidor respondió ${res.status}.`);
        const blob = await res.blob();
        if (vivo) ponerThumb(blob);
      })
      .catch((err: any) => vivo && setError(`No se pudo revisar la foto: ${err?.message || err}`))
      .finally(() => vivo && setLoading(false));
    return () => {
      vivo = false;
    };
  }, [pharmacyId, ponerThumb]);

  useEffect(() => () => setThumb((prev) => { if (prev) URL.revokeObjectURL(prev); return null; }), []);

  const subir = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Eso no es una imagen. Usá PNG, JPG o WEBP.");
      return;
    }
    setError(null);
    setConfirmar(false);
    try {
      setStep("optimizing");
      const jpg = await aJpgHistoria(file);
      setStep("uploading");
      const res = await adminFetch("/api/historia-foto/" + pharmacyId, {
        method: "PUT",
        headers: { "Content-Type": "image/jpeg" },
        body: jpg,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || `El servidor respondió ${res.status}.`);
      }
      ponerThumb(jpg);
      toast.show("Foto de historia guardada");
    } catch (err: any) {
      setError(`No se subió: ${err?.message || err}`);
    } finally {
      setStep("idle");
    }
  };

  const quitar = async () => {
    setError(null);
    setConfirmar(false);
    setStep("removing");
    try {
      const res = await adminFetch("/api/historia-foto/" + pharmacyId, { method: "DELETE" });
      if (!res.ok && res.status !== 404) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || `El servidor respondió ${res.status}.`);
      }
      ponerThumb(null);
      toast.show("Foto de historia quitada");
    } catch (err: any) {
      setError(`No se quitó: ${err?.message || err}`);
    } finally {
      setStep("idle");
    }
  };

  const estado = loading
    ? "Revisando…"
    : step === "optimizing"
      ? "Optimizando…"
      : step === "uploading"
        ? "Subiendo…"
        : step === "removing"
          ? "Quitando…"
          : thumb
            ? "Cargada"
            : "Todavía no tiene foto de historia";

  return (
    <Field label="Foto de historia" htmlFor={`ph-historia-${pharmacyId}`} hint="Vertical, 1080×1920. Es la fachada que sale en la historia de Instagram.">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file && !busy) subir(file);
        }}
        className={cx(
          "flex flex-wrap items-center gap-3 rounded-xl border p-2.5 transition-colors",
          dragOver ? "border-adm-acento bg-adm-acento-suave" : error ? "border-adm-rojo-borde bg-adm-rojo-suave" : "border-adm-border bg-adm-card",
        )}
      >
        <div className="grid h-24 w-[54px] shrink-0 place-items-center overflow-hidden rounded-md border border-adm-border bg-adm-surface text-adm-t40">
          {loading || (busy && step !== "removing") ? (
            <Spinner />
          ) : thumb ? (
            <img src={thumb} alt="Foto de historia" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon {...ICON} />
          )}
        </div>
        <div className="min-w-0 flex-1 basis-40 text-[13px]">
          <div className={cx(error ? "text-adm-rojo" : "text-adm-t55")}>{error || estado}</div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1">
          {thumb && !busy && !loading && (
            confirmar ? (
              <>
                <span className="text-[13px] text-adm-t70">¿Quitar?</span>
                <Button variant="ghost" tone="danger" size="sm" onClick={quitar}>
                  Sí
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmar(false)}>
                  No
                </Button>
              </>
            ) : (
              <Button variant="ghost" tone="danger" size="sm" onClick={() => setConfirmar(true)}>
                Quitar foto
              </Button>
            )
          )}
          <Button size="sm" disabled={busy || loading} onClick={() => inputRef.current?.click()}>
            {thumb ? "Cambiar foto" : "Subir foto"}
          </Button>
        </div>
        <input
          ref={inputRef}
          id={`ph-historia-${pharmacyId}`}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) subir(file);
          }}
        />
      </div>
    </Field>
  );
};
